import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";

// Public landing page for the Terms of Use & Medical Disclaimer.
// Renders the same CMS-managed text (scope=policy, subject=terms_and_medical_disclaimer,
// field=text) that the enrollment step used to show in a modal. Linked with target=_blank
// from the enrollment "I agree to the Terms" checkbox so parents can open it without
// leaving the flow.
const FALLBACK = `Terms of Use & Medical Disclaimer

The final text has not been published yet. Please contact Breathe·Thrive support if you need a copy before it is available here.`;

export default function Terms() {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/content/overrides").then((r) => {
      if (!alive) return;
      const val = r?.data?.policy?.terms_and_medical_disclaimer?.text;
      setText(typeof val === "string" && val.trim() ? val : FALLBACK);
      setLoaded(true);
    }).catch(() => {
      if (!alive) return;
      setText(FALLBACK);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-warm-white text-ink-dark" data-testid="terms-page">
      <header className="border-b border-edge bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-home-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-300 to-rose-300 flex items-center justify-center">
              <ShieldCheck size={18} className="text-navy" />
            </div>
            <div className="font-display font-bold text-navy">Breathe·Thrive</div>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-mauve font-bold">Policy</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-mauve font-bold">Legal</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-navy mt-1">Terms of Use &amp; Medical Disclaimer</h1>
        <p className="text-ink-mid mt-2 text-sm">Please read carefully before continuing your enrollment.</p>

        <article
          data-testid="terms-body"
          className="mt-8 bg-white rounded-3xl border border-edge shadow-sm px-6 md:px-10 py-8 text-[15px] leading-relaxed whitespace-pre-line"
        >
          {loaded ? text : "Loading…"}
        </article>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="terms-print"
            className="px-5 py-2.5 rounded-full text-sm font-semibold border border-edge hover:bg-black/5"
          >
            Print
          </button>
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = "/enroll"))}
            data-testid="terms-back"
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-navy text-warm-white hover:bg-navy-deep"
          >
            Back to enrollment
          </button>
        </div>
      </main>
    </div>
  );
}
